import { useMemo, useState } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useDebounce } from '@backrs_ui/grow-ui/src/hooks/useDebounce';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  AutocompleteOption,
  useFetchAutocompleteOptionsQuery,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { useGetSchoolFitQuery } from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.api';
import {
  MAX_SCHOOLS,
  SCHOOL_SEARCH_DEBOUNCE_MS,
  SCHOOL_SEARCH_MIN_LENGTH,
  SCHOOL_SEARCH_RESULT_LIMIT,
  TARGET_SCORE_RANGE,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.constants';
import {
  addSchool,
  removeSchool,
  selectBoosterTargets,
  updateTargets,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.slice';
import {
  ReportFacts,
  ReportSchool,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';

import {
  Autocomplete,
  Box,
  Chip,
  Divider,
  IconButton,
  SxProps,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';

import { FitRow, SchoolFitTable } from '../components/SchoolFitTable';
import { SectionCard, SectionGroup } from '../components/SectionCard';
import { useTargetSchoolAnalysis } from '../hooks/useTargetSchoolAnalysis';
import {
  stepChipRowSx,
  stepContainerSx,
  stepDividerSx,
  stepErrorSx,
  stepFieldColumnSx,
  stepIntroBodySx,
  stepMutedSx,
  stepNotSavedSx,
  stepTitleSx,
} from '../satActBooster.styles';
import { ordinal } from '../utils/format';
import { deriveSchoolContext } from '../utils/schoolContext';
import { getTargetsErrors } from '../utils/stepNavigation';

const STRINGS = appStrings.modules.satActBooster;

const SCHOOLS_LOOKUP_ENDPOINT = '/profile/lookups/schools';

type FitSchool = ReportSchool;

/** A searchable school. The lookup's name already carries its city/state, so it stands alone. */
type SchoolOption = Readonly<{
  institutionId: number | null;
  name: string;
}>;

/**
 * Institution ids are positive integers; the `> 0` bound also rejects the 0 that a missing id
 * coerces to. A school without one still resolves as an option, just with nothing to tier.
 */
const toSchoolOption = (option: AutocompleteOption): SchoolOption | null => {
  const name = 'school' in option ? option.school : option.label;

  if (!name) return null;

  const id = Number('institution_id' in option ? option.institution_id : null);

  return { institutionId: Number.isInteger(id) && id > 0 ? id : null, name };
};

/** Names embed the campus location, so a term can open a word after punctuation as well as a space. */
const WORD_BOUNDARY = /[\s(\-,.]/;

/** Lower sorts first: the term opening the name, then opening any word in it, then anywhere. */
const matchRank = (name: string, term: string): number => {
  const position = name.toLowerCase().indexOf(term);

  if (position === 0) return 0;

  return position > 0 && WORD_BOUNDARY.test(name[position - 1]) ? 1 : 2;
};

type TargetsStepProps = Readonly<{
  reportFacts: ReportFacts | null;
  /** True while the run has not yet been persisted, so the notice can say so. */
  saveDeferred?: boolean;
  /** Reveal all required-field errors once the user has tried to build the plan. */
  submitAttempted: boolean;
}>;

export const TargetsStep = ({
  reportFacts,
  submitAttempted,
  saveDeferred = false,
}: TargetsStepProps) => {
  const dispatch = useAppDispatch();
  const targets = useAppSelector(selectBoosterTargets);
  // The report resolves which test the plan targets (the stronger one for a "Both" taker).
  const resolvedTest = reportFacts?.test ?? targets.targetTest;
  const currentScore = reportFacts?.currentScore ?? null;

  // Only linked schools have an institution profile to tier against.
  const institutionIds = useMemo(
    () =>
      targets.schools
        .map((school) => school.institutionId)
        .filter((id): id is number => id !== null),
    [targets.schools],
  );

  // The report is computed before schools are picked, so its `schools` is empty here. Read the
  // tiers + mid-50% ranges separately, keyed on the linked institutions so an unrelated field
  // edit (hours, days) doesn't refetch. `data` holds the previous rows while a new set loads.
  const {
    data: fitSchools,
    isFetching: isFitFetching,
    isUninitialized: isFitUninitialized,
    isError: isFitError,
  } = useGetSchoolFitQuery(
    resolvedTest && institutionIds.length > 0
      ? { test: resolvedTest, currentScore, institutionIds }
      : skipToken,
  );

  const fitByInstitutionId = useMemo(
    () => new Map((fitSchools ?? []).map((school) => [school.institutionId, school])),
    [fitSchools],
  );

  // Uninitialized counts as pending: the query is skipped until a test and a linked school
  // resolve, so no row has been read yet.
  const isFitPending = isFitFetching || isFitUninitialized;

  useTargetSchoolAnalysis({
    schools: targets.schools,
    fitByInstitutionId,
    isFitPending,
    hasFitError: isFitError,
    test: resolvedTest,
    currentScore,
    targetScore: targets.targetScore,
    recommendedGoal: reportFacts?.recommendedGoal ?? null,
  });

  const fitRows: FitRow[] = targets.schools.map((school) => {
    const computed =
      school.institutionId === null ? null : (fitByInstitutionId.get(school.institutionId) ?? null);

    return {
      name: school.name,
      computed,
      isPending: computed === null && school.institutionId !== null && isFitFetching,
    };
  });

  // A read that never yielded any data leaves nothing to tier; drop the table rather than
  // render an empty shell. A failed refetch keeps the last rows.
  const isFitTableEmpty = isFitError && fitSchools === undefined;
  const computedSchools = fitRows
    .map((row) => row.computed)
    .filter((school): school is FitSchool => school !== null);
  const schoolContext = resolvedTest
    ? deriveSchoolContext(computedSchools, resolvedTest, targets.targetScore)
    : null;

  const [schoolInput, setSchoolInput] = useState('');
  const debouncedSchoolInput = useDebounce(schoolInput, SCHOOL_SEARCH_DEBOUNCE_MS);
  const typedTerm = schoolInput.trim();
  const searchTerm = debouncedSchoolInput.trim();

  // Skipping below the floor also keeps the lookup from rejecting the term outright.
  const isTermSearchable = searchTerm.length >= SCHOOL_SEARCH_MIN_LENGTH;

  // `currentData` is scoped to the term in the argument, so a slower earlier term can never
  // land in the list; it reads as empty until this term answers, which the pending flag covers.
  const {
    currentData: lookupResults,
    isFetching,
    isError,
  } = useFetchAutocompleteOptionsQuery(
    {
      endpoint: SCHOOLS_LOOKUP_ENDPOINT,
      searchTerm,
      displayProperty: 'school',
      filter: 'post_secondary',
      minSearchLength: SCHOOL_SEARCH_MIN_LENGTH,
    },
    { skip: !isTermSearchable },
  );

  // The lookup returns its substring matches unranked.
  const schoolOptions = useMemo(() => {
    const term = searchTerm.toLowerCase();

    return (
      lookupResults?.find((result) => result.endpoint === SCHOOLS_LOOKUP_ENDPOINT)?.values ?? []
    )
      .map(toSchoolOption)
      .filter((option): option is SchoolOption => option !== null)
      .sort(
        (a, b) =>
          matchRank(a.name, term) - matchRank(b.name, term) ||
          a.name.length - b.name.length ||
          a.name.localeCompare(b.name),
      )
      .slice(0, SCHOOL_SEARCH_RESULT_LIMIT);
  }, [lookupResults, searchTerm]);

  // Covers the debounce gap as well as the request, so the list never reads as settled early.
  const isSearchPending =
    typedTerm.length >= SCHOOL_SEARCH_MIN_LENGTH && (typedTerm !== searchTerm || isFetching);

  // A failed lookup must not read as "no matches" — that tells the user their school isn't listed.
  const noOptionsText =
    typedTerm.length < SCHOOL_SEARCH_MIN_LENGTH
      ? STRINGS.targets.schoolSearchHint(SCHOOL_SEARCH_MIN_LENGTH)
      : isError
        ? STRINGS.targets.schoolSearchError
        : STRINGS.targets.schoolSearchEmpty;

  const percentile = reportFacts?.percentile.value ?? null;
  const range = resolvedTest ? TARGET_SCORE_RANGE[resolvedTest] : null;

  const adjustTarget = (delta: number) => {
    if (!range) return;

    const base = targets.targetScore ?? currentScore ?? range.min;

    dispatch(
      updateTargets({ targetScore: Math.min(range.max, Math.max(range.min, base + delta)) }),
    );
  };

  const schoolsFull = targets.schools.length >= MAX_SCHOOLS;
  const errors = getTargetsErrors(targets);

  return (
    <Box sx={stepContainerSx}>
      {saveDeferred && (
        <Typography
          variant="text-sm-regular"
          sx={stepNotSavedSx}
          id="booster-targets-not-saved-notice"
        >
          {STRINGS.report.notSaved}
        </Typography>
      )}
      <SectionCard>
        <Box>
          <Typography variant="text-lg-semibold" sx={stepTitleSx}>
            {STRINGS.targets.title}
          </Typography>
          <Typography variant="text-sm-regular" sx={stepIntroBodySx}>
            {STRINGS.targets.body}
          </Typography>
        </Box>

        <SectionGroup label={STRINGS.targets.targetScore}>
          {currentScore !== null && (
            <Typography variant="text-sm-regular" sx={stepMutedSx}>
              {STRINGS.targets.current(
                currentScore,
                percentile != null ? STRINGS.targets.currentPct(ordinal(percentile)) : '',
              )}
            </Typography>
          )}
          <Box sx={stepperRowSx}>
            <IconButton
              id="booster-target-score-decrement"
              aria-label={STRINGS.targets.decrementAria}
              onClick={() => adjustTarget(resolvedTest === 'sat' ? -10 : -1)}
            >
              <AppIcon iconName="Minus" diameter="20px" color="Colors/Foreground/fg-secondary" />
            </IconButton>
            <Typography variant="display-sm-semibold" sx={targetValueSx}>
              {targets.targetScore ?? STRINGS.scoreField.emptyPlaceholder}
            </Typography>
            <IconButton
              id="booster-target-score-increment"
              aria-label={STRINGS.targets.incrementAria}
              onClick={() => adjustTarget(resolvedTest === 'sat' ? 10 : 1)}
            >
              <AppIcon iconName="Plus" diameter="20px" color="Colors/Foreground/fg-secondary" />
            </IconButton>
          </Box>
          {submitAttempted && errors.targetScore && range ? (
            <Typography variant="text-sm-regular" sx={stepErrorSx}>
              {STRINGS.targets.targetScoreRange(range.min, range.max)}
            </Typography>
          ) : (
            <Typography variant="text-sm-regular" sx={stepMutedSx}>
              {resolvedTest ? STRINGS.targets.targetHint(resolvedTest) : ''}
            </Typography>
          )}
          {schoolContext && targets.targetScore !== null && (
            <Typography
              variant="text-sm-medium"
              sx={contextLineSx}
              id="booster-school-context-line"
            >
              {schoolContext.kind === 'reached'
                ? STRINGS.targets.schoolContext.reached(
                    schoolContext.school ?? STRINGS.targets.fitTable.unknownSchool,
                    schoolContext.low,
                    targets.targetScore,
                  )
                : STRINGS.targets.schoolContext.nearest(
                    schoolContext.school ?? STRINGS.targets.fitTable.unknownSchool,
                    schoolContext.low,
                    schoolContext.pointsAway,
                  )}
            </Typography>
          )}
        </SectionGroup>

        <Divider sx={stepDividerSx} />

        <SectionGroup
          label={STRINGS.targets.schoolsLabel(MAX_SCHOOLS)}
          supportingText={STRINGS.targets.schoolsSupporting}
        >
          <Autocomplete
            id="booster-school-search"
            sx={stepFieldColumnSx}
            options={schoolOptions}
            getOptionLabel={(option) => option.name}
            inputValue={schoolInput}
            disabled={schoolsFull}
            filterOptions={(options) => options}
            loading={isSearchPending}
            loadingText={STRINGS.targets.schoolSearchLoading}
            noOptionsText={noOptionsText}
            onInputChange={(_event, value) => setSchoolInput(value)}
            onChange={(_event, value) => {
              if (value) {
                dispatch(addSchool({ institutionId: value.institutionId, name: value.name }));
                setSchoolInput('');
              }
            }}
            value={null}
            renderInput={(params) => (
              <TextField {...params} placeholder={STRINGS.targets.schoolSearchPlaceholder} />
            )}
          />
          {targets.schools.length > 0 && (
            <Box sx={stepChipRowSx}>
              {targets.schools.map((school) => (
                <Chip
                  key={school.name}
                  id={`booster-school-${school.institutionId ?? school.name}`}
                  label={school.name}
                  onDelete={() => dispatch(removeSchool(school.name))}
                />
              ))}
            </Box>
          )}
          {targets.schools.length > 0 && resolvedTest && !isFitTableEmpty && (
            <SchoolFitTable rows={fitRows} currentScore={currentScore} test={resolvedTest} />
          )}
        </SectionGroup>
      </SectionCard>
    </Box>
  );
};

const contextLineSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-brand-primary'],
});

const stepperRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-xl'],
  width: 'fit-content',
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  borderRadius: backrs.radius['radius-md'],
  paddingInline: backrs.spacing['spacing-sm'],
  paddingBlock: backrs.spacing['spacing-xs'],
});

const targetValueSx: SxProps<Theme> = ({ backrs }) => ({
  minWidth: '96px',
  textAlign: 'center',
  color: backrs.colors['Colors/Text/text-primary'],
});
